import { useEffect, useRef, useState } from "react";
import { Loader2, MessageSquareText, Mail, ArrowLeft, ShieldCheck } from "lucide-react";

const T = {
  light: {
    title: "text-slate-900", sub: "text-slate-500", input: "border-slate-300 bg-white text-slate-900 focus:border-amber-500",
    btn: "bg-slate-900 hover:bg-slate-800 text-white", link: "text-amber-700 hover:text-amber-900", muted: "text-slate-400",
    check: "accent-amber-600", box: "bg-amber-50 border-amber-200 text-amber-900",
  },
  dark: {
    title: "text-white", sub: "text-zinc-400", input: "border-zinc-700 bg-zinc-800 text-white focus:border-amber-500",
    btn: "bg-amber-500 hover:bg-amber-400 text-zinc-950", link: "text-amber-400 hover:text-amber-300", muted: "text-zinc-500",
    check: "accent-amber-500", box: "bg-zinc-800 border-zinc-700 text-zinc-200",
  },
};

/** Second sign-in step: the 6-digit code from a text (or email). Shared by the app login and the Back Office. */
export function MfaStep({ challenge, onVerify, onResend, onBack, theme = "light", testPrefix = "mfa" }) {
  const t = T[theme];
  const [code, setCode] = useState("");
  const [trust, setTrust] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [wait, setWait] = useState(challenge.resend_after_sec || 30);
  const inputRef = useRef(null);
  const submitted = useRef("");

  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => {
    if (wait <= 0) return undefined;
    const id = setInterval(() => setWait((w) => w - 1), 1000);
    return () => clearInterval(id);
  }, [wait]);

  async function verify(value) {
    if (busy || value.length !== 6 || submitted.current === value) return;
    submitted.current = value;
    setBusy(true); setError("");
    const r = await onVerify(value, trust);
    setBusy(false);
    if (!r.ok) { setError(r.error); setCode(""); submitted.current = ""; inputRef.current?.focus(); }
  }

  async function resend(channel) {
    setBusy(true); setError("");
    const r = await onResend(channel);
    setBusy(false);
    if (!r.ok) setError(r.error);
    else setWait(r.challenge?.resend_after_sec || 30);
  }

  const viaText = challenge.channel === "sms";
  const Icon = viaText ? MessageSquareText : Mail;
  return (
    <div className="space-y-5" data-testid={`${testPrefix}-step`}>
      <div>
        <div className="flex items-center gap-2 mb-2">
          <ShieldCheck className="w-5 h-5 text-amber-500" />
          <h2 className={`text-xl font-display font-bold ${t.title}`}>Check your {viaText ? "texts" : "email"}</h2>
        </div>
        <p className={`text-sm ${t.sub}`} data-testid={`${testPrefix}-destination`}>
          <Icon className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
          We sent a 6-digit code to <b>{challenge.destination}</b>. It expires in 10 minutes.
        </p>
      </div>
      <div>
        <input
          ref={inputRef} value={code} inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]*" maxLength={6}
          placeholder="••••••" aria-label="Sign-in code" data-testid={`${testPrefix}-code-input`}
          onChange={(e) => { const v = e.target.value.replace(/\D/g, "").slice(0, 6); setCode(v); if (v.length === 6) verify(v); }}
          className={`w-full rounded-lg border px-4 py-3 text-center text-3xl tracking-[0.5em] font-mono focus:outline-none ${t.input}`}
        />
        {error && <p className="text-sm text-rose-600 mt-2" role="alert" data-testid={`${testPrefix}-error`}>{error}</p>}
      </div>
      <label className={`flex items-start gap-2 text-sm rounded-lg border px-3 py-2.5 ${t.box}`}>
        <input type="checkbox" checked={trust} onChange={(e) => setTrust(e.target.checked)} className={`mt-0.5 ${t.check}`} data-testid={`${testPrefix}-trust-checkbox`} />
        <span>Trust this device for 30 days — no code next time you sign in here.</span>
      </label>
      <button type="button" onClick={() => verify(code)} disabled={busy || code.length !== 6} data-testid={`${testPrefix}-verify-btn`}
        className={`w-full rounded-md py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60 ${t.btn}`}>
        {busy && <Loader2 className="w-4 h-4 animate-spin" />} Verify & sign in
      </button>
      <div className={`flex flex-wrap items-center justify-between gap-2 text-xs ${t.muted}`}>
        <button type="button" onClick={onBack} className={`inline-flex items-center gap-1 ${t.link}`} data-testid={`${testPrefix}-back-btn`}>
          <ArrowLeft className="w-3 h-3" /> Use a different account
        </button>
        <div className="flex items-center gap-3">
          <button type="button" disabled={busy || wait > 0} onClick={() => resend()} className={`${t.link} disabled:opacity-50`} data-testid={`${testPrefix}-resend-btn`}>
            {wait > 0 ? `Resend in ${wait}s` : "Resend code"}
          </button>
          {viaText && challenge.can_email && (
            <button type="button" disabled={busy || wait > 0} onClick={() => resend("email")} className={`${t.link} disabled:opacity-50`} data-testid={`${testPrefix}-email-instead-btn`}>
              Email me instead
            </button>
          )}
          {!viaText && challenge.can_sms && (
            <button type="button" disabled={busy || wait > 0} onClick={() => resend("sms")} className={`${t.link} disabled:opacity-50`} data-testid={`${testPrefix}-text-instead-btn`}>
              Text me instead
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
